import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import useCompanyStore from "../../store/useCompanyStore";
import TextError from "../../components/Reusable/TextError";

const PostNewJob = () => {
  const { companyList, fetchCompanies } = useCompanyStore();
  const navigate = useNavigate();

  useEffect(() => {
    fetchCompanies();
  }, []);

  const companyOptions = companyList.map((c) => ({
    value: c.id,
    label: c.company_name,
  }));

  const validationSchema = Yup.object({
    jobPosition: Yup.string().required("Job Position is required"),
    companyName: Yup.object()
      .shape({
        value: Yup.string().required(),
        label: Yup.string().required(),
      })
      .nullable()
      .required("Company is required"),
    jobDetails: Yup.mixed()
      .required("File is required")
      .test("fileSize", "File too large (max 25MB)", (value) => {
        return value && value.size <= 25 * 1024 * 1024;
      })
      .test("fileType", "Unsupported file type", (value) => {
        return (
          value &&
          ["application/pdf", "image/png", "image/jpg", "image/jpeg"].includes(
            value.type,
          )
        );
      }),
  });

  const initialValues = {
    jobPosition: "",
    companyName: null,
    jobDetails: null,
  };

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: "45px",
      fontSize: "12px",
      borderRadius: "8px",
      borderColor: state.isFocused ? "#4B5563" : "#D1D5DB",
      boxShadow: "none",
      "&:hover": {
        borderColor: "#6B7280",
      },
    }),
  };

  const handleSubmit = async (values) => {
    const accessToken = localStorage.getItem("accessToken");
    const apiBaseUrl = process.env.REACT_APP_BASE_URL;

    try {
      const myHeaders = new Headers();
      myHeaders.append("Authorization", `Bearer ${accessToken}`);

      const formData = new FormData();
      formData.append("company_id", values.companyName.value);
      formData.append("name", values.jobPosition);
      formData.append("image", values.jobDetails);
      formData.append("posting_date", new Date().toISOString().split("T")[0]);

      const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: formData,
        redirect: "follow",
      };

      const res = await fetch(`${apiBaseUrl}/job-positions`, requestOptions);

      if (!res.ok) {
        const err = await res.text();
        console.error(err);
        return;
      }

      const result = await res.text();
      // console.log(result);

      navigate("/publishjob");
    } catch (error) {
      console.error("Error submitting:", error);
    }
  };

  return (
    <div className="board__layout">
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {(formik) => (
          <>
            <div className="section-header">
              <h1>Post New Job</h1>
            </div>

            <Form className="flex flex-col gap-4 bg-[#f5f5f5]">
              {/* Job Position */}
              <div className="p-6 bg-white rounded-lg">
                <label className="text-lg font-normal text-medium-dark-gray">
                  Job Position
                </label>
                <Field
                  type="text"
                  name="jobPosition"
                  placeholder="Enter the job position"
                />
                <ErrorMessage name="jobPosition" component={TextError} />
              </div>

              {/* Company Name (react-select) */}
              <div className="p-6 bg-white rounded-lg">
                <label className="text-lg font-normal text-medium-dark-gray">
                  Company Name
                </label>

                <Select
                  name="companyName"
                  options={companyOptions}
                  value={formik.values.companyName}
                  onChange={(selected) =>
                    formik.setFieldValue("companyName", selected)
                  }
                  onBlur={() => formik.setFieldTouched("companyName", true)}
                  placeholder="Select a company"
                  styles={selectStyles}
                />

                <ErrorMessage name="companyName" component={TextError} />
              </div>

              {/* File Upload */}
              <div className="p-6 bg-white rounded-lg">
                <label className="text-lg font-normal text-medium-dark-gray">
                  Upload File
                </label>

                <input
                  type="file"
                  name="jobDetails"
                  onChange={(e) => {
                    formik.setFieldValue("jobDetails", e.target.files[0]);
                  }}
                />

                <label className="text-sm font-light text-desc-gray">
                  Upload a single document with all job details. PDF, PNG, JPG
                  accepted. Max size: 25 MB.
                </label>

                <ErrorMessage name="jobDetails" component={TextError} />
              </div>

              <div className="flex gap-4 justify-end">
                <button className="w-24 bg-white border-2 border-primary-hrms text-sm font-semibold text-primary-hrms  hover:text-primary-hrms/80">
                  Cancel
                </button>

                <button
                  className="bg-primary-hrms text-white px-3 py-2.5 text-sm font-semibold rounded-sm hover:bg-green-600 hover:text-white transition-all"
                  type="submit"
                >
                  Publish Job &gt;&gt;
                </button>
              </div>
            </Form>
          </>
        )}
      </Formik>
    </div>
  );
};

export default PostNewJob;
